/**
 * useAliExpressCategories Hook
 *
 * Centralized category management for AliExpress/Coupang integration
 * Provides category keywords mapping, build categories filtering
 */

import { useCategories } from '../api/client';

// Category keywords mapping for AliExpress API (derived from DB data)
export function useCategoryKeywords() {
  const { data: categories, isLoading, error } = useCategories();

  const categoryKeywords: Record<string, { keywords: string; name: string; coupangQuery: string }> = {};

  if (categories) {
    for (const cat of categories) {
      categoryKeywords[cat.slug] = {
        keywords: cat.keywords || cat.name,
        name: cat.name,
        coupangQuery: cat.coupangQuery || cat.name
      };
    }
  }

  return { categoryKeywords, isLoading, error };
}

// Get build component categories only (PC build feature)
export function useBuildCategories() {
  const { data: categories } = useCategories();

  const buildCategories = (categories || [])
    .filter(cat => cat.isBuildComponent)
    .map(cat => ({
      id: cat.slug,
      name: cat.name,
      required: true // Could be extended in DB if needed
    }));

  return buildCategories;
}

// Get sidebar categories for navigation
export function useSidebarCategories() {
  const { data: categories, isLoading, error } = useCategories();

  const sidebarCategories = (categories || []).map(cat => ({
    id: cat.slug,
    name: cat.name,
    icon: cat.icon || 'Package',
    keywords: cat.keywords || cat.name
  }));

  // Add "All" category at the beginning
  const allCategory = { id: 'all', name: '전체', icon: 'Package', keywords: 'computer PC' };

  return {
    categories: [allCategory, ...sidebarCategories],
    isLoading,
    error
  };
}

// Get category by slug
export function useCategoryBySlug(slug: string | undefined) {
  const { data: categories, isLoading, error } = useCategories();

  const category = categories?.find(cat => cat.slug === slug);

  return { category, isLoading, error };
}

export default useCategoryKeywords;
